import { ChangeEvent, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUpdateProductMutation, useLazyGetProductByIdQuery } from '@modules/product';
import { IImage } from '@app/types/image';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';
import { useDeleteImageMutation } from '@modules/images';
import { getImageUrl } from '@app/helpers/getUploadUrl';

import { editProductSchema } from './data';
import { EditProductSate, IUpdateProductFormData } from './types';
import {
  getProductData,
  handleDeleteBase64Image,
  onChangeImageUploadInput,
  handleDeleteImageFromFirebase,
} from './logicHelper';

export const useEditProductLogic = () => {
  const { productId = '' } = useParams()
  const [deleteImage] = useDeleteImageMutation()
  const [getProductById] = useLazyGetProductByIdQuery();
  const [updateProductById] = useUpdateProductMutation();

  const [state, setState] = useState<EditProductSate>({
    imageUrl: '',
    isSuccess: '',
    isLoading: true,
    largeImageMessage: '',
    isDeleteModalOpen: false,
    actionOnImageString: '',
    isViewImageModalOpen: false,
    firebaseImageForProcess: {} as IImage,
  })

  const methods = useForm<IUpdateProductFormData>({
    // @ts-ignore
    resolver: yupResolver(editProductSchema),
    mode: 'onSubmit',
    defaultValues: {
      newUploadingImages: {} as FileList,
    }
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods

  const receivedImagesFromBackend = watch('images')
  const newUploadingImages = watch('newUploadingImages')
  const previews = useRenderFileViewHook(newUploadingImages)

  const onSubmit = async (data: IUpdateProductFormData) => {
    setState((prev) => ({ ...prev, isSuccess: '', isLoading: true }));

    try {
      const formData = new FormData();
      // eslint-disable-next-line max-len
      const fields: Array<keyof IUpdateProductFormData> = [
        'titleAm',
        'descriptionAm',
        'titleEn',
        'descriptionEn',
        'titleRu',
        'descriptionRu',
        'visible',
        'brand'
      ];

      // eslint-disable-next-line max-len
      fields.forEach((field) => {
        if (typeof data[field] === 'string') {
          formData.append(field, data[field] as string);
        }
      });

      const { newUploadingImages: newUploadedImage } = data;

      if (newUploadedImage) {
        Object.values(newUploadedImage).forEach((image) => formData.append('files[]', image));
      }

      await updateProductById({ formData, id: productId }).unwrap();

      getProductData({ reset, productId, setState, getProductById });
      setState((prev) => ({ ...prev, isSuccess: 'Product has been successfully updated', largeImageMessage: '' }));
    } catch (error) {
      console.log('Error: ', error);
      setState((prev) => ({
        ...prev,
        largeImageMessage: '',
        isLoading: false,
        isSuccess: 'Something went wrong'
      }));
    }
  };

  useEffect(() => {
    if (state.firebaseImageForProcess?.url) {
      setState((prev) => ({ ...prev, imageUrl: getImageUrl(prev.firebaseImageForProcess?.url) }))
    }
  }, [state.firebaseImageForProcess])

  useEffect(() => {
    if (!productId) {
      return;
    }

    getProductData({ reset, productId, setState, getProductById });
  }, [])

  return {
    ...state,
    methods,
    errors,
    previews,
    receivedImagesFromBackend,
    setState,
    onSubmit,
    register,
    handleSubmit,
    getProductData: () => getProductData({ reset, productId, setState, getProductById }),
    handleDeleteBase64Image: (base64: string) => handleDeleteBase64Image({
      base64,
      previews,
      setValue,
      setState,
      newUploadingImages
    }),
    onChangeImageUploadInput: (event: ChangeEvent<HTMLInputElement>) => onChangeImageUploadInput({
      event,
      setState,
      setError,
      newUploadingImages,
      setValue,
      receivedImagesFromBackend
    }),
    handleDeleteImageFromFirebase: () => handleDeleteImageFromFirebase({
      state,
      productId,
      reset,
      setState,
      deleteImage,
      getProductById,
    }),
  }
}
